import { useState, type FormEvent } from "react";
import {
  useCreateTaxType,
  useTaxTypes,
  useUpdateTaxType,
  type TaxType,
  type TaxTypeSyncResult,
} from "../api/hooks";
import { DataTable, createColumnHelper } from "../components/DataTable";
import { Button, Chip, ErrorNotice, Field, Input, Modal, Notice, PageHeader, Textarea } from "../components/ui";
import { IconPlus } from "../components/icons";

/**
 * Tipos fiscales (IVA, IGIC, IPSI...). Solo super-admin llega aquí (menú, ruta y servidor lo comprueban).
 *
 * Cada tenant tiene asignado uno y su porcentaje se SUMA al neto de sus facturas (los precios de los
 * planes son siempre sin impuestos). Un tipo no se borra: se desactiva para que deje de ofrecerse, y
 * los tenants que ya lo tienen lo conservan. Si cambia la etiqueta o el porcentaje, se actualiza en
 * Stripe para las facturas FUTURAS; las ya emitidas no se tocan.
 */

interface TaxTypeForm {
  name: string;
  label: string;
  percentage: string;
  legalNote: string;
  isDefault: boolean;
  isActive: boolean;
}

const EMPTY_FORM: TaxTypeForm = {
  name: "",
  label: "IVA",
  percentage: "21",
  legalNote: "",
  isDefault: false,
  isActive: true,
};

const column = createColumnHelper<TaxType>();

function fmtPercentage(value: number | string): string {
  return `${Number(value).toFixed(2)} %`;
}

export function TaxTypesPage() {
  const query = useTaxTypes();
  const create = useCreateTaxType();
  const update = useUpdateTaxType();

  const [createOpen, setCreateOpen] = useState(false);
  const [createValues, setCreateValues] = useState<TaxTypeForm>({ ...EMPTY_FORM });

  const [editType, setEditType] = useState<TaxType | null>(null);
  const [editValues, setEditValues] = useState<TaxTypeForm>({ ...EMPTY_FORM });
  const [syncResult, setSyncResult] = useState<TaxTypeSyncResult | null>(null);

  const openCreate = (): void => {
    create.reset();
    setCreateValues({ ...EMPTY_FORM });
    setCreateOpen(true);
  };

  const openEdit = (taxType: TaxType): void => {
    update.reset();
    setSyncResult(null);
    setEditType(taxType);
    setEditValues({
      name: taxType.name,
      label: taxType.label,
      percentage: String(Number(taxType.percentage)),
      legalNote: taxType.legalNote ?? "",
      isDefault: taxType.isDefault,
      isActive: taxType.isActive,
    });
  };

  const onCreateSubmit = (event: FormEvent): void => {
    event.preventDefault();
    create.mutate(
      {
        name: createValues.name.trim(),
        label: createValues.label.trim(),
        percentage: parseFloat(createValues.percentage) || 0,
        legalNote: createValues.legalNote.trim() || null,
        isDefault: createValues.isDefault,
        isActive: createValues.isActive,
      },
      { onSuccess: () => setCreateOpen(false) },
    );
  };

  const onEditSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (!editType) return;
    update.mutate(
      {
        id: editType.id,
        values: {
          name: editValues.name.trim(),
          label: editValues.label.trim(),
          percentage: parseFloat(editValues.percentage) || 0,
          legalNote: editValues.legalNote.trim() || null,
          // El predeterminado no se puede quitar desde su propia ficha: se marca otro.
          ...(editType.isDefault ? {} : { isDefault: editValues.isDefault }),
          isActive: editValues.isActive,
        },
      },
      {
        onSuccess: (result) => {
          // Si el cambio se reenvió a Stripe, se muestra el resultado antes de cerrar.
          if (result.sync.total > 0) setSyncResult(result.sync);
          else setEditType(null);
        },
      },
    );
  };

  const columns = [
    column.accessor("name", { header: "Nombre" }),
    column.accessor("label", { header: "Etiqueta en factura" }),
    column.accessor("percentage", {
      header: "Porcentaje",
      cell: (info) => <span className="num">{fmtPercentage(info.getValue())}</span>,
    }),
    column.accessor("legalNote", {
      header: "Mención legal",
      cell: (info) =>
        info.getValue() ? (
          <span className="block max-w-xs truncate" title={info.getValue() ?? undefined}>
            {info.getValue()}
          </span>
        ) : (
          <span className="text-ink-400">—</span>
        ),
    }),
    column.accessor("tenantCount", {
      header: "Tenants",
      cell: (info) => <span className="num">{info.getValue() ?? 0}</span>,
    }),
    column.accessor("isActive", {
      header: "Estado",
      cell: (info) => {
        const taxType = info.row.original;
        return (
          <div className="flex items-center gap-2">
            {info.getValue() ? <Chip tone="ok">Activo</Chip> : <Chip tone="neutral">Inactivo</Chip>}
            {taxType.isDefault ? <Chip tone="neutral">Predeterminado</Chip> : null}
          </div>
        );
      },
    }),
    column.display({
      id: "edit",
      header: "",
      cell: (info) => <Button onClick={() => openEdit(info.row.original)}>Editar</Button>,
    }),
  ];

  return (
    <>
      <PageHeader
        eyebrow="Facturación"
        title="Tipos fiscales"
        description="Impuestos que se suman al precio de los planes (que es siempre neto). Cada tenant tiene asignado un tipo y su porcentaje se aplica a sus facturas futuras. El tipo predeterminado se asigna a los tenants nuevos que no indican otro. Un tipo no se borra: se desactiva y los tenants que ya lo tienen lo conservan."
        actions={
          <Button variant="primary" onClick={openCreate}>
            <IconPlus className="h-4 w-4" />
            Nuevo tipo
          </Button>
        }
      />

      {query.isError ? (
        <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <DataTable
          columns={columns}
          data={query.data ?? []}
          isLoading={query.isLoading}
          emptyMessage="Todavía no hay tipos fiscales."
        />
      )}

      <Modal open={createOpen} title="Nuevo tipo fiscal" onClose={() => setCreateOpen(false)}>
        <form onSubmit={onCreateSubmit} className="space-y-3">
          <TaxTypeFields values={createValues} onChange={setCreateValues} idPrefix="tt-new" />
          {create.isError ? <ErrorNotice error={create.error} /> : null}
          <div className="flex gap-2">
            <Button type="submit" variant="primary" disabled={create.isPending}>
              {create.isPending ? "Creando…" : "Crear tipo"}
            </Button>
            <Button onClick={() => setCreateOpen(false)}>Cancelar</Button>
          </div>
        </form>
      </Modal>

      <Modal open={editType !== null} title="Editar tipo fiscal" onClose={() => setEditType(null)}>
        {syncResult ? (
          <div className="space-y-3">
            <Notice tone={syncResult.failed > 0 ? "warn" : "ok"}>
              {syncResult.failed > 0
                ? `Guardado. El nuevo impuesto se envió a Stripe en ${syncResult.total - syncResult.failed} de ${syncResult.total} suscripciones; ${syncResult.failed} fallaron (vuelve a guardar para reintentar).`
                : `Guardado. El nuevo impuesto se envió a Stripe en ${syncResult.total} suscripciones y se aplicará a sus facturas futuras.`}
            </Notice>
            <Button onClick={() => setEditType(null)}>Cerrar</Button>
          </div>
        ) : (
          <form onSubmit={onEditSubmit} className="space-y-3">
            {editType && editType.tenantCount > 0 ? (
              <Notice tone="neutral">
                {`Lo usan ${editType.tenantCount} tenants. Si cambias la etiqueta o el porcentaje, se actualiza en Stripe para sus facturas futuras; las ya emitidas no se tocan.`}
              </Notice>
            ) : null}
            <TaxTypeFields
              values={editValues}
              onChange={setEditValues}
              idPrefix="tt-edit"
              defaultLocked={editType?.isDefault ?? false}
            />
            {update.isError ? <ErrorNotice error={update.error} /> : null}
            <div className="flex gap-2">
              <Button type="submit" variant="primary" disabled={update.isPending}>
                {update.isPending ? "Guardando…" : "Guardar cambios"}
              </Button>
              <Button onClick={() => setEditType(null)}>Cancelar</Button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}

function TaxTypeFields({
  values,
  onChange,
  idPrefix,
  defaultLocked = false,
}: {
  values: TaxTypeForm;
  onChange: (values: TaxTypeForm) => void;
  idPrefix: string;
  /** El tipo ya es el predeterminado: no se puede quitar desde aquí (se marca otro). */
  defaultLocked?: boolean;
}) {
  const set = (partial: Partial<TaxTypeForm>) => onChange({ ...values, ...partial });
  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Nombre" htmlFor={`${idPrefix}-name`} hint="Cómo lo ves al elegirlo en un tenant.">
          <Input
            id={`${idPrefix}-name`}
            required
            value={values.name}
            onChange={(e) => set({ name: e.target.value })}
            placeholder="Península y Baleares (IVA)"
          />
        </Field>
        <Field label="Etiqueta en factura" htmlFor={`${idPrefix}-label`} hint="Lo que ve el cliente: IVA, IGIC, IPSI…">
          <Input
            id={`${idPrefix}-label`}
            required
            maxLength={30}
            value={values.label}
            onChange={(e) => set({ label: e.target.value })}
            placeholder="IVA"
          />
        </Field>
        <Field label="Porcentaje (%)" htmlFor={`${idPrefix}-pct`} hint="Se SUMA al neto. 0 = no se aplica impuesto.">
          <Input
            id={`${idPrefix}-pct`}
            type="number"
            min="0"
            max="100"
            step="0.01"
            required
            value={values.percentage}
            onChange={(e) => set({ percentage: e.target.value })}
          />
        </Field>
      </div>
      <Field
        label="Mención legal en la factura"
        htmlFor={`${idPrefix}-note`}
        hint="Texto que se imprime en la factura con valor fiscal de los tenants con este tipo (p. ej. operaciones no sujetas a IVA). Vacío = ninguna."
      >
        <Textarea
          id={`${idPrefix}-note`}
          rows={4}
          maxLength={600}
          value={values.legalNote}
          onChange={(e) => set({ legalNote: e.target.value })}
          placeholder="Operación no sujeta al IVA español por reglas de localización…"
        />
      </Field>
      <label className="flex items-center gap-2 text-sm text-ink-700 dark:text-ink-200">
        <input type="checkbox" checked={values.isActive} onChange={(e) => set({ isActive: e.target.checked })} />
        Activo (disponible para asignar a tenants)
      </label>
      <label className="flex items-center gap-2 text-sm text-ink-700 dark:text-ink-200">
        <input
          type="checkbox"
          checked={values.isDefault}
          disabled={defaultLocked}
          onChange={(e) => set({ isDefault: e.target.checked })}
        />
        {defaultLocked
          ? "Es el predeterminado (para cambiarlo, márcalo en otro tipo)"
          : "Predeterminado para los tenants nuevos (quita la marca al actual)"}
      </label>
    </>
  );
}
